/**
 * XVI Toolbar component
 */
import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  HostBinding,
  Input,
  SimpleChange,
  OnChanges,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { filter, findIndex, get, isEmpty, isFunction } from 'lodash';
import { Observable } from 'rxjs';

import {
  IToolbarButtonDefinition,
  ToolbarButtonDefinition,
} from '@shared/types';
import { asObservable } from '@shared/utils';
import { observableOf, ReplaySubject, rx } from '@shared/rx';
import { BsDropdownDirective } from 'ngx-bootstrap';

@Component({
  selector: 'xvi-toolbar',
  templateUrl: 'toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XVIToolbarComponent implements DoCheck, OnChanges {
  @Input() public buttons: IToolbarButtonDefinition[] = [];
  @Input() public click?: (value: unknown, event: MouseEvent | object) => void;
  @Input() public disabled: boolean;
  @Input() public title: string;
  @Input() public defaultBtnClass?: string = '';

  @Input()
  @HostBinding('class.--align-right')
  public alignRight: boolean = false;

  @ViewChildren(BsDropdownDirective)
  public dropdownDirectives: QueryList<BsDropdownDirective>;

  protected checkers: (() => void)[] = [];
  protected disablingMap: Map<ToolbarButtonDefinition, Observable<boolean>> =
    new Map();

  public get defaultBtnMarginClass(): string {
    if (!isEmpty(this.defaultBtnClass)) return `${this.defaultBtnClass} `;

    return '';
  }
  public ngDoCheck(): void {
    this.checkers.forEach((checker) => checker());
  }

  public ngOnChanges(
    changes: Record<keyof XVIToolbarComponent, SimpleChange>,
  ): void {
    if (changes.buttons) {
      this.checkers = [];
      this.disablingMap.clear();

      this.buttons?.forEach((btn) => {
        let disabled$: Observable<boolean>;

        if (this.disabled) disabled$ = observableOf(true);
        else if (isFunction(btn.disabled)) {
          const subj$ = new ReplaySubject<boolean>(1);
          const checker = (): void =>
            subj$.next((btn.disabled as () => boolean)());
          this.checkers.push(checker);
          disabled$ = subj$.pipe(rx.distinctUntilChanged());
        } else if (btn.disabledObs) {
          disabled$ = btn.disabledObs;
        } else disabled$ = asObservable(btn.disabled);

        this.disablingMap.set(btn, disabled$);
      });
    }
  }

  public handleBtnClick(
    btn: IToolbarButtonDefinition,
    rootBtn?: IToolbarButtonDefinition,
    event?: MouseEvent | object,
  ): void {
    let value = 'value' in btn ? btn.value : btn.caption;

    if (btn.expandable) {
      // If the button is expandable, do not pass a value to avoid triggering a click action.
      value = null;
    }

    const selfClickIsValid = isFunction(this.click);
    const btnClickIsValid = isFunction(btn.click);

    const dropdownDirectiveArray = this.dropdownDirectives.toArray();

    const btnWithChildItems = filter(
      this.buttons,
      (button) => !isEmpty(button.childItems),
    );
    const curRootBtn = rootBtn || btn;
    const curRootBtnIndex = findIndex(btnWithChildItems, curRootBtn);
    const curRootBtnDropdownDirective = get(
      dropdownDirectiveArray,
      curRootBtnIndex,
    );

    if (
      curRootBtnDropdownDirective?.isOpen &&
      (selfClickIsValid || btnClickIsValid)
    ) {
      curRootBtnDropdownDirective.hide();
    }

    if (selfClickIsValid) {
      this.click(value, event);
    }
    if (btnClickIsValid) {
      btn.click(value, event);
    }
  }

  public isDisabled(button: ToolbarButtonDefinition): Observable<boolean> {
    if (this.disabled) return observableOf(this.disabled);

    return this.disablingMap.get(button) ?? observableOf(true);
  }

  public getButtonValue(
    index: number,
    button: ToolbarButtonDefinition,
  ): string {
    // Usually buttons don't have id and value will be used as a unique identifier,
    // however, giving precedence to the "id" property will allow in future
    // to have multiple buttons with the very same value
    return get(button, 'id', get(button, 'value', null));
  }

  public trackByChildItemsValue(
    index: number,
    item: ToolbarButtonDefinition,
  ): string {
    const childItemsLength = item.childItems?.length;

    return childItemsLength
      ? item.childItems[childItemsLength - 1].value
      : item.value;
  }

  public trackByCaptionOrValue(
    index: number,
    item: ToolbarButtonDefinition,
  ): string {
    return item.caption || item.value;
  }
}
